import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function CellPotentialCalculator() {
  const [cathode, setCathode] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Cell Potential Calculator", url: "https://calclab.app/calculator/cell-potential" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Cell Potential Calculator",
      "Calculate standard cell potential for electrochemical reactions. Free online electrochemistry calculator.",
      "https://calclab.app/calculator/cell-potential",
      lang
    )
  ]);
  const [anode, setAnode] = useState("");
  const [result, setResult] = useState<{ ecell: number; spontaneity: string } | null>(null);

  const calculate = () => {
    const eCathode = parseFloat(cathode);
    const eAnode = parseFloat(anode);

    if (isNaN(eCathode) || isNaN(eAnode)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    const ecell = eCathode - eAnode;
    const spontaneity = ecell > 0 ? "Spontaneous (Galvanic Cell)" : "Non-spontaneous (Electrolytic Cell)";

    setResult({ ecell, spontaneity });

    toast({
      title: "Calculation Complete",
      description: `E°cell = ${ecell.toFixed(3)} V`,
    });
  };

  const reset = () => {
    setCathode("");
    setAnode("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Cell Potential Calculator" 
      calculatorPath="/calculator/cell-potential"
      category="chemistry"
      subcategory="Electrochemistry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Cell Potential Calculator</CardTitle>
            <CardDescription>
              Calculate standard cell potential (E°cell)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cathode">Cathode Potential E°(cathode) (V)</Label>
                <Input
                  id="cathode"
                  type="number"
                  step="any"
                  value={cathode}
                  onChange={(e) => setCathode(e.target.value)}
                  placeholder="Reduction potential (cathode)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="anode">Anode Potential E°(anode) (V)</Label>
                <Input
                  id="anode"
                  type="number"
                  step="any"
                  value={anode}
                  onChange={(e) => setAnode(e.target.value)}
                  placeholder="Reduction potential (anode)"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <div className="space-y-4">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Standard Cell Potential</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{result.ecell.toFixed(3)} V</p>
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Spontaneity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg">{result.spontaneity}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-mono">E°cell = E°(cathode) - E°(anode)</p>
                <p className="text-sm text-muted-foreground">
                  • E°cell &gt; 0: Spontaneous (Galvanic)<br />
                  • E°cell &lt; 0: Non-spontaneous (Electrolytic)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
